import React, { useMemo, memo } from 'react';
import formStyles from '../../../form/Form.module.css';

import * as ProdutoAPI from '../../../../api/produto';
import { flash } from '../../../../helpers/flash';

/* utils */
import { formatarNumero } from '../../../../utils/masks';

/* Componentes UI */
import Input from '../../../form/Input';
import InputNumber from '../../../form/InputNumber';
import TextArea from '../../../form/TextArea';
import DataList from '../../../form/DataList';
import Checkbox from '../../../form/Checkbox';
import Select from '../../../form/Select';

/* MUI + MRT */
import { Box, Button, Tooltip, IconButton, ThemeProvider } from '@mui/material';
import { Edit, Delete, Print } from '@mui/icons-material';
import { MaterialReactTable } from 'material-react-table';
import { MRT_Localization_PT_BR } from 'material-react-table/locales/pt-BR';

import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import InputAdornment from '@mui/material/InputAdornment';

import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    FormControlLabel,
    Switch,
    MenuItem,
    TextField,
    Autocomplete,
} from '@mui/material';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';

import { podeEditar, podeImprimir, podeCancelar } from '../../../../hooks/usePermisionMRT';

const InventarioSection = ({ module, dados, setFlashMessage, theme, mrt }) => {
    const { state, actions } = module;
    const { novoItem, Ambiente, OptionsAmbiente, NCM, OptionsNCMInv } = state;
    const bem = dados?.produtoBens || {};

    const [editPdfUuid, setEditPdfUuid] = React.useState(null);
    const [editPdfNome, setEditPdfNome] = React.useState('');
    const [confirm, setConfirm] = React.useState({ open: false, kind: null, item: null });

    const [nfDialog, setNfDialog] = React.useState({ open: false, table: null });
    const [nfNome, setNfNome] = React.useState(''); // texto digitado
    const [nfEscolhido, setNfEscolhido] = React.useState(''); // opção escolhida
    // kind: 'img' | 'pdf'

    const columns = useMemo(
        () => [
            {
                id: 'inventario',
                header: 'Itens do Inventário',
                columns: [
                    { accessorKey: 'Ambiente.descricao', id: 'Ambiente.descricao', header: 'Ambiente' },
                    {
                        accessorKey: 'descricao',
                        id: 'descricao',
                        header: 'Descrição',
                        //you can access a cell in many callback column definition options like this
                        muiTableBodyCellProps: ({ row, cell }) => ({
                            onClick: () => {
                                console.info(row.original);
                            },
                        }),
                    },
                    {
                        accessorKey: 'valor',
                        header: 'Valor',
                        muiTableBodyCellProps: {
                            align: 'right',
                        },
                        accessorFn: (row) =>
                            row.valorUnitario === null
                                ? ''
                                : 'R$ ' +
                                  parseFloat(row.valor)?.toLocaleString('pt-BR', {
                                      style: 'decimal',
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                      minimumIntegerDigits: 1,
                                      useGrouping: true,
                                  }),
                    },
                    {
                        accessorKey: 'ativo',
                        header: 'Ativo',
                        columnFilterModeOptions: ['equals'],
                        filterSelectOptions: [
                            { label: 'Todos', value: '' },
                            { label: 'Ativo', value: 1 },
                            { label: 'Inativo', value: 0 },
                        ],
                        filterVariant: 'select',
                        muiTableBodyCellProps: ({ row }) => ({
                            align: 'center',
                            onClick: () => {},
                        }),
                        Cell: ({ row }) => {
                            return row.original.ativo ? 'Ativo' : 'Inativo';
                        },
                    },
                    {
                        id: 'produtoBensInventarioNFs[0].descricao',
                        header: 'PDF NF',

                        // Pega o primeiro PDF do array (se existir)
                        accessorFn: (row) => row?.produtoBensInventarioNFs?.[0]?.descricao ?? '',

                        muiTableBodyCellProps: ({ row }) => {
                            const desc = row?.original?.produtoBensInventarioNFs?.[0]?.descricao;

                            const msg =
                                'Nome do arquivo copiado para a área de transferência. Agora selecione os itens da mesma nota fiscal e clique em "Arquivo NF".';

                            return {
                                align: 'left',

                                onClick: async () => {
                                    if (!desc) {
                                        flash.warning('Nenhum PDF disponível para copiar.');
                                        return;
                                    }

                                    try {
                                        await navigator.clipboard.writeText(desc);
                                        flash.info(msg);
                                    } catch (err) {
                                        console.error('Erro ao copiar:', err);
                                        flash.error('Erro ao copiar nome do arquivo.');
                                    }
                                },

                                style: {
                                    cursor: desc ? 'pointer' : 'default',
                                    textDecoration: desc ? 'underline dotted' : 'none', // 👌 leve feedback visual
                                },

                                title: desc ? `Clique para copiar o nome do arquivo ${desc}` : 'Sem PDF disponível',
                            };
                        },

                        Cell: ({ cell }) => cell.getValue() || 'Sem PDF',
                    },
                ],
            },
        ], //eslint-disable-next-line
        [] //getCommonEditTextFieldProps
    );
    const pdfOptions = useMemo(() => {
        const invs = bem?.produtoBensInventarios || [];
        const all = invs.flatMap((it) => it?.produtoBensInventarioNFs || []);
        const descricoes = all.map((x) => x?.descricao).filter(Boolean);

        // remove duplicados
        return [...new Set(descricoes)];
    }, [bem?.produtoBensInventarios]);

    return (
        <Box sx={{ mt: 4 }}>
            <div className={formStyles.preview_bandeira}>
                <h4>Inventário / Mobília</h4>
            </div>

            {/* --- ÁREA DE DRAFT (INCLUSÃO/EDIÇÃO) --- */}
            <Box>
                <div className={formStyles.stiloPadrao}>
                    <TextArea
                        text="Descrição do Item"
                        name="inventario.descricao"
                        handleOnChange={actions.draft.change}
                        value={novoItem.descricao || ''}
                        linhas="3"
                    />
                </div>

                <div className={formStyles.row} style={{ alignItems: 'flex-end' }}>
                    <Input
                        text="Fotos"
                        type="file"
                        name="images"
                        inputWidth="400px"
                        handleOnChange={(e) => actions.draft.handleDraftFileChange(e, 'draftImagens')}
                        multiple={true}
                        accept=".jpg, .jpeg, .png"
                    />
                    <Input
                        text="NF (PDF)"
                        type="file"
                        name="arquivo"
                        inputWidth="400px"
                        handleOnChange={(e) => actions.draft.handleDraftFileChange(e, 'draftNfs')}
                        multiple={true}
                        accept="application/pdf"
                    />
                </div>

                {/* Previews dos arquivos (Estilo antigo adaptado) */}
                {(novoItem.draftImagens || []).map((img) => (
                    <Box key={img.tempId} sx={{ border: '1px solid #ddd', borderRadius: 1, p: 1, width: 170 }}>
                        <Tooltip title="Clique para abrir">
                            <img
                                src={img.previewUrl}
                                width={150}
                                height={90}
                                style={{ objectFit: 'cover', cursor: 'pointer', opacity: img.ativo ? 1 : 0.4 }}
                                onClick={() => window.open(img.previewUrl, '_blank')}
                            />
                        </Tooltip>

                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                            <IconButton
                                size="small"
                                color="error"
                                onClick={() => actions.draft.removeDraftAsset('draftImagens', img.tempId)}
                                title="Remover do draft"
                            >
                                <DeleteForeverIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    </Box>
                ))}
                {(novoItem.draftNfs || []).map((nf) => (
                    <Box key={nf.tempId} sx={{ border: '1px solid #ddd', borderRadius: 1, p: 1, width: 170 }}>
                        <Tooltip title="Clique para abrir o PDF">
                            <Box
                                onClick={() => window.open(nf.previewUrl, '_blank')}
                                sx={{
                                    height: 90,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                }}
                            >
                                <embed
                                    src={nf.previewUrl}
                                    type="application/pdf"
                                    width="150"
                                    height="90"
                                    style={{ pointerEvents: 'none', border: '1px dashed #bbb', borderRadius: 6 }}
                                />
                            </Box>
                        </Tooltip>

                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                            <IconButton
                                size="small"
                                color="error"
                                onClick={() => actions.draft.removeDraftAsset('draftNfs', nf.tempId)}
                                title="Remover do draft"
                            >
                                <DeleteForeverIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    </Box>
                ))}
                {/* 2. ARQUIVOS DO BANCO (item selecionado) */}
                <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {/* IMAGENS */}
                    {(novoItem.produtoBensInventarioImagens || []).map((img) => {
                        const src = `${process.env.REACT_APP_API}/imagens/${img.descricao}`;

                        return (
                            <Box key={img.uuid} sx={{ border: '1px solid #ddd', borderRadius: 1, p: 1, width: 170 }}>
                                <Tooltip title="Clique para abrir">
                                    <img
                                        src={src}
                                        width={150}
                                        height={90}
                                        style={{ objectFit: 'cover', cursor: 'pointer', opacity: img.ativo ? 1 : 0.4 }}
                                        onClick={() => window.open(src, '_blank')}
                                    />
                                </Tooltip>

                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
                                    <FormControlLabel
                                        label="Ativo"
                                        control={
                                            <Switch
                                                size="small"
                                                checked={!!img.ativo}
                                                onChange={(e) =>
                                                    actions.assets.editarInventarioImagem({
                                                        uuid: img.uuid,
                                                        ativo: e.target.checked,
                                                        posicao: img.posicao || 'Padrão',
                                                    })
                                                }
                                            />
                                        }
                                    />

                                    <IconButton
                                        size="small"
                                        color="error"
                                        onClick={() => setConfirm({ open: true, kind: 'img', item: img })}
                                        title="Deletar imagem"
                                    >
                                        <DeleteForeverIcon fontSize="small" />
                                    </IconButton>
                                </Box>

                                <Select
                                    name="posicao"
                                    text="Posição"
                                    value={img.posicao || 'Padrão'}
                                    isEnum={true}
                                    options={state.EnumPosicao}
                                    handleOnChange={(e) =>
                                        actions.assets.editarInventarioImagem({
                                            uuid: img.uuid,
                                            ativo: !!img.ativo,
                                            posicao: e.target.value,
                                        })
                                    }
                                />
                            </Box>
                        );
                    })}

                    {/* PDFs */}
                    {(novoItem.produtoBensInventarioNFs || []).map((nf) => {
                        const src = `${process.env.REACT_APP_API}/pdf/${nf.descricao}`;
                        const isEditing = editPdfUuid === nf.uuid;

                        return (
                            <Box key={nf.uuid} sx={{ border: '1px solid #ddd', borderRadius: 1, p: 1, width: 170 }}>
                                <Tooltip title="Clique para abrir o PDF">
                                    <Box
                                        onClick={() => window.open(src, '_blank')}
                                        sx={{
                                            height: 90,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            cursor: 'pointer',
                                            opacity: nf.ativo ? 1 : 0.4,
                                        }}
                                    >
                                        <embed
                                            src={src}
                                            type="application/pdf"
                                            width="150"
                                            height="90"
                                            style={{
                                                pointerEvents: 'none',
                                                border: '1px dashed #bbb',
                                                borderRadius: 6,
                                            }}
                                        />
                                    </Box>
                                </Tooltip>

                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
                                    <FormControlLabel
                                        label="Ativo"
                                        control={
                                            <Switch
                                                size="small"
                                                checked={!!nf.ativo}
                                                onChange={(e) =>
                                                    actions.assets.editarInventarioPDF({
                                                        uuid: nf.uuid,
                                                        ativo: e.target.checked,
                                                        // mantém o nome atual (se o backend aceitar, ele preserva)
                                                        descricao: nf.descricao,
                                                    })
                                                }
                                            />
                                        }
                                    />

                                    <IconButton
                                        size="small"
                                        color="error"
                                        onClick={() => setConfirm({ open: true, kind: 'pdf', item: nf })}
                                        title="Deletar PDF"
                                    >
                                        <DeleteForeverIcon fontSize="small" />
                                    </IconButton>
                                </Box>

                                {/* Nome do PDF (duplo clique para renomear) */}
                                {isEditing ? (
                                    <Box sx={{ mt: 0.5, display: 'flex', gap: 1, alignItems: 'center' }}>
                                        <TextField
                                            size="small"
                                            value={editPdfNome}
                                            onChange={(e) => setEditPdfNome(e.target.value)}
                                            onKeyDown={async (e) => {
                                                if (e.key === 'Escape') {
                                                    setEditPdfUuid(null);
                                                    setEditPdfNome('');
                                                    return;
                                                }
                                                if (e.key === 'Enter') {
                                                    const novoNome = (editPdfNome || '').trim();
                                                    if (!novoNome) return;

                                                    await actions.assets.editarInventarioPDF({
                                                        uuid: nf.uuid,
                                                        ativo: !!nf.ativo,
                                                        descricao: novoNome,
                                                    });

                                                    setEditPdfUuid(null);
                                                    setEditPdfNome('');
                                                }
                                            }}
                                            fullWidth
                                        />

                                        <Button
                                            size="small"
                                            variant="contained"
                                            onClick={async () => {
                                                const novoNome = (editPdfNome || '').trim();
                                                if (!novoNome) return;

                                                await actions.assets.editarInventarioPDF({
                                                    uuid: nf.uuid,
                                                    ativo: !!nf.ativo,
                                                    descricao: novoNome,
                                                });

                                                setEditPdfUuid(null);
                                                setEditPdfNome('');
                                            }}
                                        >
                                            OK
                                        </Button>
                                    </Box>
                                ) : (
                                    <Tooltip title="Duplo clique para renomear">
                                        <Box
                                            sx={{
                                                fontSize: 12,
                                                mt: 0.5,
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                cursor: 'text',
                                                userSelect: 'none',
                                            }}
                                            onDoubleClick={() => {
                                                setEditPdfUuid(nf.uuid);
                                                setEditPdfNome(nf.descricao || '');
                                            }}
                                        >
                                            {nf.descricao}
                                        </Box>
                                    </Tooltip>
                                )}
                            </Box>
                        );
                    })}
                </Box>
                <div className={formStyles.stiloPadrao}>
                    <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                        <DataList
                            text="NCM"
                            name="inventario.NCM.descricao"
                            filteredOptions={OptionsNCMInv}
                            handleOnChange={actions.draft.change}
                            onBlur={actions.draft.confirmNcmSelection}
                            value={NCM?.descricao || ''}
                            inputWidth="400px"
                        />
                        <DataList
                            text="Ambiente"
                            name="inventario.Ambiente.descricao"
                            filteredOptions={OptionsAmbiente}
                            handleOnChange={actions.draft.change}
                            value={Ambiente?.descricao || ''}
                        />
                        <InputNumber
                            text="Valor"
                            name="inventario.valor"
                            handleOnChange={actions.draft.change}
                            value={novoItem.valor ? formatarNumero(novoItem.valor) : ''}
                        />
                        <Input
                            text="Data Compra"
                            type="date"
                            name="inventario.dataCompra"
                            handleOnChange={actions.draft.change}
                            value={novoItem.dataCompra || ''}
                        />
                        <Checkbox
                            text="Ativo"
                            name="inventario.ativo"
                            handleOnChange={actions.draft.change}
                            checked={!!novoItem.ativo}
                        />

                        <Button
                            variant="contained"
                            color="inherit"
                            onClick={() => actions.draft.cleanNovoItem(state.baseItem)}
                            style={{ marginLeft: '5px' }}
                        >
                            Limpar
                        </Button>
                        <Button
                            variant="contained"
                            color={novoItem.funcao === 'Deletar' ? 'error' : 'primary'}
                            onClick={actions.draft.salvarItem}
                            disabled={!novoItem.descricao ? true : false}
                            style={{ marginLeft: '5px' }}
                        >
                            {novoItem.funcao || 'Adicionar'}
                        </Button>
                    </Box>
                </div>
            </Box>

            {/* Dialog de confirmação (IMG/PDF) */}
            <Dialog
                open={confirm.open}
                onClose={() => setConfirm({ open: false, kind: null, item: null })}
                fullWidth
                maxWidth="xs"
            >
                <DialogTitle>Confirmar exclusão</DialogTitle>

                <DialogContent>
                    <DialogContentText>
                        {confirm.kind === 'img'
                            ? 'Deseja realmente deletar esta imagem?'
                            : confirm.kind === 'pdf'
                              ? 'Deseja realmente deletar este PDF?'
                              : 'Deseja realmente deletar este item?'}
                    </DialogContentText>
                </DialogContent>

                <DialogActions>
                    <Button onClick={() => setConfirm({ open: false, kind: null, item: null })}>Cancelar</Button>

                    <Button
                        color="error"
                        variant="contained"
                        onClick={async () => {
                            const item = confirm.item;
                            try {
                                if (confirm.kind === 'img') {
                                    await actions.assets.deletarInventarioImagem?.({ uuid: item?.uuid });
                                } else if (confirm.kind === 'pdf') {
                                    await actions.assets.deletarInventarioPdf?.({ uuid: item?.uuid });
                                }
                                await actions.grid.refresh?.();
                            } catch (e) {
                                console.error(e);
                                setFlashMessage?.('Não foi possível excluir.', 'error');
                            } finally {
                                setConfirm({ open: false, kind: null, item: null });
                            }
                        }}
                    >
                        Deletar
                    </Button>
                </DialogActions>
            </Dialog>

            {/* --- GRID MRT --- */}
            <ThemeProvider theme={theme}>
                <MaterialReactTable
                    // --- Configurações de Estado (Vindas do Objeto MRT) ---
                    displayColumnDefOptions={{
                        'mrt-row-actions': {
                            muiTableHeadCellProps: {
                                align: 'center',
                            },
                            size: 100,
                        },
                        'mrt-row-numbers': {
                            enableColumnOrdering: true, //turn on some features that are usually off
                            enableResizing: true,
                            muiTableHeadCellProps: {
                                sx: {
                                    fontSize: '1.2rem',
                                },
                            },
                        },
                        'mrt-row-select': {
                            enableColumnActions: false,
                            enableHiding: true,
                            size: 50,
                        },
                    }}
                    enableColumnFilterModes
                    enableColumnOrdering
                    columns={columns}
                    data={bem.produtoBensInventarios || []}
                    getRowId={(row) => row.uuid || row.tempId}
                    rowCount={mrt.rowCount ?? bem.produtoBensInventarios?.length ?? 0}
                    enableRowSelection
                    manualFiltering
                    manualPagination
                    manualSorting
                    state={{
                        columnFilters: mrt.columnFilters,
                        columnFilterFns: mrt.columnFilterFns,
                        globalFilter: mrt.globalFilter,
                        pagination: mrt.pagination,
                        sorting: mrt.sorting,
                        isLoading: mrt.isLoading,
                        showAlertBanner: mrt.isError,
                        showProgressBars: mrt.isRefetching,
                    }}
                    initialState={{
                        showColumnFilters: true,
                        density: 'compact',
                    }}
                    onColumnFiltersChange={mrt.setColumnFilters}
                    onGlobalFilterChange={mrt.setGlobalFilter}
                    onPaginationChange={mrt.setPagination}
                    onSortingChange={mrt.setSorting}
                    // --- UI e Ações de Linha ---
                    enableEditing
                    renderRowActions={({ row }) => (
                        <Box sx={{ display: 'flex', gap: '1rem' }}>
                            <Tooltip arrow placement="left" title="Editar">
                                <IconButton
                                    onClick={() => actions.draft.selectItem(row.original, row.index, 'Editar')}
                                    disabled={!podeEditar()}
                                >
                                    <Edit />
                                </IconButton>
                            </Tooltip>
                            <Tooltip arrow placement="right" title="Deletar">
                                <IconButton
                                    color="error"
                                    onClick={() => actions.draft.selectItem(row.original, row.index, 'Deletar')}
                                    disabled={!podeCancelar()}
                                >
                                    <Delete />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    )}
                    // --- Ações de Topo (Impressão e Reaproveitar NF) ---
                    renderTopToolbarCustomActions={({ table }) => (
                        <div style={{ display: 'flex', gap: '8px' }}>
                            <Tooltip arrow placement="top-end" title="Imprimir Inventário">
                                <Button
                                    color="info"
                                    variant="contained"
                                    onClick={actions.assets.handleImprimirInventario}
                                    disabled={!podeImprimir()}
                                >
                                    Imprimir
                                </Button>
                            </Tooltip>
                            <Tooltip
                                arrow
                                placement="top-end"
                                title="Reaproveitar arquivo de nota fiscal para itens selecionados"
                            >
                                <Button
                                    color="success"
                                    variant="contained"
                                    onClick={() => setNfDialog({ open: true, table })}
                                    disabled={
                                        !podeEditar() || (!table.getIsSomeRowsSelected() && !table.getIsAllPageRowsSelected())
                                    }
                                >
                                    Arquivo NF
                                </Button>
                            </Tooltip>
                        </div>
                    )}
                    muiToolbarAlertBannerProps={
                        mrt.isError
                            ? {
                                  color: 'error',
                                  children: 'Error loading data',
                              }
                            : undefined
                    }
                    positionExpandColumn={'first'}
                    positionActionsColumn={'first'}
                    positionToolbarAlertBanner="bottom"
                    localization={MRT_Localization_PT_BR}
                />
            </ThemeProvider>
        </Box>
    );
};

export default memo(InventarioSection);
